import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronLeft, ChevronRight, Inbox, MoreVertical, X } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { isBottomNavVisible } from '../layout/bottomNavPaths';
import { APP_BAR_OFFSET } from '../layout/mobileShell';
import TableIconAction from './TableIconAction';

// Cabeçalho e linhas do skeleton reaproveitam exatamente a casca visual da
// tabela real (Paper/TableCell abaixo, no corpo de `DefaultTable`) — cor,
// radius, borda e divisórias vêm do tema, não de um gradiente azul fixo que
// só funcionava no modo claro e não existe em nenhum protótipo aprovado.
// `colunms` reaproveita os rótulos reais das colunas: só as células do corpo
// viram placeholder.
const SKELETON_ROWS = 6;

const TableSkeleton = ({ colunms = [], hasActions = true, embedded = false }) => {
    const headerCellSx = {
        backgroundColor: 'transparent',
        color: 'text.disabled',
        fontWeight: 700,
        fontSize: '10.5px',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        borderBottom: 'none',
        py: 1.75,
        px: 2
    };

    return (
        <Paper sx={{
            borderRadius: embedded ? 0 : (t) => `${t.shape.borderRadius}px`,
            boxShadow: 'none',
            overflow: 'hidden',
            border: embedded ? 'none' : '1px solid',
            borderColor: 'divider'
        }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow sx={{
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: '16px',
                                right: '16px',
                                bottom: 0,
                                height: '1px',
                                backgroundColor: 'divider'
                            }
                        }}>
                            {colunms.map(({ path, name, style, invisible }) => !invisible && (
                                <TableCell key={path} style={style} align="center" sx={headerCellSx}>
                                    {name}
                                </TableCell>
                            ))}
                            {hasActions && (
                                <TableCell align="center" sx={headerCellSx}>
                                    Ações
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...Array(SKELETON_ROWS)].map((_, rowIndex) => (
                            <TableRow
                                key={rowIndex}
                                sx={{
                                    position: 'relative',
                                    ...(rowIndex < SKELETON_ROWS - 1 && {
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: '16px',
                                            right: '16px',
                                            bottom: 0,
                                            height: '1px',
                                            backgroundColor: 'divider'
                                        }
                                    })
                                }}
                            >
                                {colunms.map(({ path, style, invisible }) => !invisible && (
                                    <TableCell key={path} style={style} align="center" sx={{ py: 1.5, px: 2, borderBottom: 'none' }}>
                                        <Skeleton variant="text" width="70%" height={20} animation="wave" sx={{ mx: 'auto' }} />
                                    </TableCell>
                                ))}
                                {hasActions && (
                                    <TableCell align="center" sx={{ py: 1.5, px: 2, borderBottom: 'none' }}>
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            {[...Array(3)].map((_, actionIndex) => (
                                                <Skeleton key={actionIndex} variant="circular" width={28} height={28} animation="wave" />
                                            ))}
                                        </Stack>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                flexWrap: 'wrap',
                px: 2,
                py: 1.5
            }}>
                <Skeleton variant="text" width={180} height={18} animation="wave" />
                <Stack direction="row" spacing={1} alignItems="center">
                    <Skeleton variant="rounded" width={70} height={34} animation="wave" sx={{ borderRadius: '8px' }} />
                    <Skeleton variant="rounded" width={38} height={38} animation="wave" sx={{ borderRadius: '10px' }} />
                    <Skeleton variant="text" width={90} height={18} animation="wave" />
                    <Skeleton variant="rounded" width={38} height={38} animation="wave" sx={{ borderRadius: '10px' }} />
                </Stack>
            </Box>
        </Paper>
    );
};

// Rodapé de paginação — réplica de `.pagination`/`.pg-info`/`.pg-controls`/
// `.icon-btn` do `prototipo-lista-vendas.html`, não o `TablePagination`
// padrão do MUI (layout/rótulos diferentes: "Mostrando X–Y de Z" à esquerda,
// select de tamanho de página + botões quadrados com borda + "Página N de M"
// à direita).
const PaginationFooter = ({ count, page, rowsPerPage, rowsPerPageOptions, onPageChange, onRowsPerPageChange, itemLabel }) => {
    const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));
    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min((page + 1) * rowsPerPage, count);

    const iconBtnSx = {
        width: 38,
        height: 38,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        flexShrink: 0,
        '&:hover': {
            backgroundColor: 'background.subtle',
            color: 'text.primary',
            borderColor: 'primary.main'
        },
        '&.Mui-disabled': {
            opacity: 0.4
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '14px',
            flexWrap: 'wrap',
            px: 2,
            py: 1.5
        }}>
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                Mostrando{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{from}–{to}</Box>
                {' '}de{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{count}</Box>
                {' '}{itemLabel}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>
                    Por página
                </Typography>
                <Select
                    value={rowsPerPage}
                    onChange={onRowsPerPageChange}
                    size="small"
                    sx={{
                        height: 34,
                        fontSize: '12.5px',
                        borderRadius: '8px',
                        backgroundColor: 'background.subtle',
                        '& .MuiSelect-select': { py: 0.5, pl: 1.25, pr: '28px !important' }
                    }}
                >
                    {rowsPerPageOptions.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ fontSize: '12.5px' }}>{opt}</MenuItem>
                    ))}
                </Select>
                <IconButton
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 0}
                    title="Página anterior"
                    sx={iconBtnSx}
                >
                    <ChevronLeft size={16} strokeWidth={2} />
                </IconButton>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'text.primary', px: '4px', whiteSpace: 'nowrap' }}>
                    Página {Math.min(page + 1, totalPages)} de {totalPages}
                </Typography>
                <IconButton
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    title="Próxima página"
                    sx={iconBtnSx}
                >
                    <ChevronRight size={16} strokeWidth={2} />
                </IconButton>
            </Box>
        </Box>
    );
};

function isActionVisibleForRow(action, row) {
    if (action.filterRow && typeof action.filterRow === 'function' && !action.filterRow(row)) {
        return false;
    }
    const rowStatusLower = row.status?.toLowerCase() || '';
    if (action.ruleShow) {
        const ruleShowArray = Array.isArray(action.ruleShow) ? action.ruleShow : [action.ruleShow];
        if (!ruleShowArray.some((rule) => rule?.toLowerCase() === rowStatusLower)) {
            return false;
        }
    }
    if (action.ruleHide) {
        const ruleHideArray = Array.isArray(action.ruleHide) ? action.ruleHide : [action.ruleHide];
        if (ruleHideArray.some((rule) => rule?.toLowerCase() === rowStatusLower)) {
            return false;
        }
    }
    return true;
}

const MAX_INLINE_ACTIONS = 1;

// Quando a linha tem mais de MAX_INLINE_ACTIONS ações visíveis, mostra só as
// 3 primeiras + um botão "⋮" com o restante num menu — evita a linha de
// ações crescer sem limite e quebrar o layout da tabela.
const RowActions = ({ actions, row, justifyContent = 'center' }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const visibleActions = actions.filter((action) => isActionVisibleForRow(action, row));
    const inlineActions = visibleActions.length > MAX_INLINE_ACTIONS
        ? visibleActions.slice(0, MAX_INLINE_ACTIONS)
        : visibleActions;
    const overflowActions = visibleActions.length > MAX_INLINE_ACTIONS
        ? visibleActions.slice(MAX_INLINE_ACTIONS)
        : [];

    const handleOverflowClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOverflowActionClick = (props) => {
        props.onClick(row);
        handleClose();
    };

    return (
        <Stack direction="row" spacing={1} justifyContent={justifyContent}>
            {inlineActions.map(({ index, icon, props }) => (
                <TableIconAction
                    key={index}
                    id={row.id}
                    onClick={() => props.onClick(row)}
                    icon={typeof icon === 'function' ? icon(row) : icon}
                    props={props}
                />
            ))}
            {overflowActions.length > 0 && (
                <>
                    {/* Gatilho é um `TableIconAction` igual aos outros da linha —
                        mesmo IconButton/Tooltip que já roda em produção nas telas
                        de listagem — em vez de um botão com casca própria, que
                        destoava visualmente ao lado dos ícones de ação normais. */}
                    <TableIconAction
                        id={row.id}
                        onClick={handleOverflowClick}
                        icon={<MoreVertical size={16} strokeWidth={2} />}
                        props={{ tooltip: 'Mais ações' }}
                    />
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        onClick={(event) => event.stopPropagation()}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 216,
                                p: '6px',
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: (t) => t.customShadows?.lg
                            }
                        }}
                        MenuListProps={{
                            sx: { display: 'flex', flexDirection: 'column', gap: '1px', py: 0 }
                        }}
                    >
                        {overflowActions.map(({ index, icon, props }) => (
                            <MenuItem
                                key={index}
                                onClick={() => handleOverflowActionClick(props)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    py: '9px',
                                    px: '10px',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'background.subtle',
                                        color: 'text.primary',
                                        '& .MuiListItemIcon-root': {
                                            color: 'primary.main'
                                        }
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 0, color: 'text.disabled' }}>
                                    {typeof icon === 'function' ? icon(row) : icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={props.tooltip || 'Ação'}
                                    primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Stack>
    );
};

// ---------------------------------------------------------------------------
// Modo mobile (shell mobile) — espelha as listagens do app Flutter (`fv-app`):
// cards de 12px com borda 1px e 14px de padding, 8px entre cards, título
// 15px/600, status à direita, campo de busca arredondado com ícone azul,
// estado vazio com ícone grande cinza e skeleton em cards (ver
// `sales_list_screen.dart`, `customers_screen.dart` e `standard_empty_state.dart`).
// O desktop/tablet (tabela) não passa por nada disto.
// ---------------------------------------------------------------------------

// Colors.blueAccent do Flutter — mesma cor usada pelo `MobileLayout`.
const MOBILE_ACCENT = '#448AFF';

const isStatusColumn = ({ path, name }) => /^status$/i.test(String(path || '')) || /status|situa[cç][aã]o/i.test(String(name || ''));

const MobileSkeleton = () => (
    <Stack spacing={1}>
        {[...Array(SKELETON_ROWS)].map((_, i) => (
            <Box
                key={i}
                sx={{
                    p: '14px',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="rounded" width="55%" height={14} animation="wave" sx={{ borderRadius: '4px' }} />
                    <Skeleton variant="rounded" width="30%" height={11} animation="wave" sx={{ borderRadius: '4px', mt: 1 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Skeleton variant="rounded" width={72} height={14} animation="wave" sx={{ borderRadius: '4px', ml: 'auto' }} />
                    <Skeleton variant="rounded" width={52} height={10} animation="wave" sx={{ borderRadius: '4px', mt: 1, ml: 'auto' }} />
                </Box>
            </Box>
        ))}
    </Stack>
);

// Todas as ações visíveis da linha num menu "⋮" (rótulo + ícone), em vez dos
// ícones soltos da tabela — alvo de toque maior e sem ambiguidade de ícone.
const MobileRowMenu = ({ actions, row }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const visibleActions = actions.filter((action) => isActionVisibleForRow(action, row));
    if (visibleActions.length === 0) return null;

    const handleClose = () => setAnchorEl(null);

    return (
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0, mr: -1, my: -0.5 }}>
            <IconButton
                aria-label="Mais ações"
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                sx={{ color: 'text.secondary' }}
            >
                <MoreVertical size={18} strokeWidth={2} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        minWidth: 200,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: (t) => t.customShadows?.lg
                    }
                }}
            >
                {visibleActions.map(({ index, icon, props }, i) => (
                    <MenuItem
                        key={index ?? i}
                        onClick={() => { handleClose(); props.onClick(row); }}
                        sx={{ minHeight: 48, gap: 1.5, fontSize: '14px', fontWeight: 500 }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, color: 'text.secondary' }}>
                            {typeof icon === 'function' ? icon(row) : icon}
                        </ListItemIcon>
                        <ListItemText primary={props.tooltip || 'Ação'} primaryTypographyProps={{ fontSize: '14px', fontWeight: 500 }} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

const MobileCard = ({ row, visibleCols, iconsActions, hasActions, onRowClick }) => {
    const statusCol = visibleCols.find(isStatusColumn);
    const titleCol = visibleCols.find((col) => col !== statusCol) || statusCol;
    const detailCols = visibleCols.filter((col) => col !== titleCol && col !== statusCol);
    const renderValue = (col) => (col.render ? col.render({ ...row }) : row[col.path]);

    return (
        <Box
            onClick={() => onRowClick && onRowClick(row)}
            sx={{
                p: '14px',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                cursor: onRowClick ? 'pointer' : 'default',
                WebkitTapHighlightColor: 'transparent',
                transition: 'background-color 0.12s ease-in-out',
                '&:active': onRowClick ? { backgroundColor: 'background.subtle' } : {}
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                    component="div"
                    sx={{ flex: 1, minWidth: 0, fontSize: '15px', fontWeight: 600, color: 'text.primary', overflowWrap: 'anywhere', '& > *': { fontSize: 'inherit !important', fontWeight: 'inherit !important' } }}
                >
                    {titleCol ? renderValue(titleCol) : null}
                </Typography>
                {statusCol && statusCol !== titleCol && (
                    <Box sx={{ flexShrink: 0, maxWidth: '48%', textAlign: 'right' }}>
                        {renderValue(statusCol)}
                    </Box>
                )}
                {hasActions && <MobileRowMenu actions={iconsActions} row={row} />}
            </Box>

            {detailCols.length > 0 && (
                <Box
                    sx={{
                        mt: 1.25,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        columnGap: 1.5,
                        rowGap: 1
                    }}
                >
                    {detailCols.map((col) => (
                        <Box key={col.path} sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'text.disabled', lineHeight: 1.3 }}>
                                {col.name}
                            </Typography>
                            <Typography
                                component="div"
                                sx={{ fontSize: '13px', color: 'text.secondary', mt: '2px', overflowWrap: 'anywhere' }}
                            >
                                {renderValue(col)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const MobileSearchField = ({ value, onChange, placeholder }) => (
    <TextField
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        fullWidth
        InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: MOBILE_ACCENT, fontSize: 22 }} />,
            endAdornment: value ? (
                <IconButton aria-label="Limpar busca" size="small" onClick={() => onChange && onChange('')}>
                    <X size={18} strokeWidth={2} />
                </IconButton>
            ) : null
        }}
        sx={{
            '& .MuiOutlinedInput-root': {
                height: 52,
                borderRadius: '12px',
                backgroundColor: 'background.paper',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'divider' },
                '&.Mui-focused fieldset': { borderColor: MOBILE_ACCENT, borderWidth: '2px' }
            }
        }}
    />
);

const MobileEmptyState = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 8, px: 3 }}>
        <Box sx={{ color: 'text.disabled', mb: 2, display: 'flex' }}>
            <Inbox size={72} strokeWidth={1.25} />
        </Box>
        <Typography sx={{ fontSize: '20px', fontWeight: 500, color: 'text.secondary' }}>
            Nenhum registro encontrado
        </Typography>
        <Typography sx={{ fontSize: '15px', color: 'text.disabled', mt: 1, maxWidth: 300 }}>
            Tente ajustar os filtros ou adicionar novos registros
        </Typography>
    </Box>
);

const px = (value) => parseFloat(value) || 0;
const MIN_LIST_HEIGHT = 240;

/**
 * Mobile: limita a altura do container da lista para que a janela não role e
 * só os cards rolem (título/filtros/busca/contador ficam fixos acima).
 * Altura = viewport - posição do container - rodapés das ancestrais - padding
 * inferior do `<main>` (que reserva a barra inferior e a safe-area). Só é
 * habilitado com a barra inferior visível; oculta, a lista flui e a janela rola.
 * Só se aplica quando nada (em fluxo) vem depois da lista até o `<main>`; em
 * telas com conteúdo abaixo (detalhes, relatórios) e em dialogs retorna `null`
 * e a página segue rolando normalmente.
 */
function useMobileBoundedList(ref, enabled) {
    const [height, setHeight] = React.useState(null);

    const measure = React.useCallback(() => {
        const el = ref.current;
        const main = el ? el.closest('main') : null;
        if (!enabled || !el || !main) {
            setHeight((cur) => (cur === null ? cur : null));
            return;
        }
        let extra = 0;
        let hasFollowing = false;
        for (let node = el; node && node !== main; node = node.parentElement) {
            if (node !== el) {
                const cs = window.getComputedStyle(node);
                extra += px(cs.paddingBottom) + px(cs.borderBottomWidth) + px(cs.marginBottom);
            }
            for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
                const position = window.getComputedStyle(sib).position;
                if (position !== 'fixed' && position !== 'absolute' && sib.offsetHeight > 0) hasFollowing = true;
            }
        }
        if (hasFollowing) {
            setHeight((cur) => (cur === null ? cur : null));
            return;
        }
        const top = el.getBoundingClientRect().top + window.scrollY;
        const next = Math.max(MIN_LIST_HEIGHT, Math.floor(window.innerHeight - top - extra - px(window.getComputedStyle(main).paddingBottom)));
        setHeight((cur) => (cur === next ? cur : next));
    }, [ref, enabled]);

    // Reavalia a cada render (conteúdo acima mudou: filtros, chips, contador).
    React.useLayoutEffect(() => {
        measure();
    });

    React.useEffect(() => {
        if (!enabled) return undefined;
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);
        const vv = window.visualViewport;
        if (vv) vv.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
            if (vv) vv.removeEventListener('resize', measure);
        };
    }, [enabled, measure]);

    // Mudança de tamanho dos blocos acima da lista (ex.: expandir filtros) sem
    // novo render desta tabela.
    React.useEffect(() => {
        const el = ref.current;
        const main = el ? el.closest('main') : null;
        if (!enabled || !main || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => measure());
        observer.observe(main);
        for (let node = el; node && node !== main; node = node.parentElement) {
            for (let sib = node.previousElementSibling; sib; sib = sib.previousElementSibling) observer.observe(sib);
        }
        return () => observer.disconnect();
    });

    return height;
}

// Se o bloco do topo (título + filtros + chips) ocupar mais que essa fração da
// altura da janela, não fixa (sobraria pouca área para a lista).
const MAX_STICKY_HEADER_RATIO = 0.45;

/**
 * Mobile, barra inferior oculta (lista fluida, a janela rola): mantém o bloco
 * que vem ANTES da tabela na página (cabeçalho da tela, FilterToolbar, chips —
 * irmãos anteriores da raiz do DefaultTable) fixo logo abaixo do AppBar.
 * Cada irmão vira `position: sticky` com `top` = AppBar + altura acumulada dos
 * anteriores (estilo inline, restaurado no cleanup). Devolve a altura total do
 * bloco, usada pelo backdrop opaco que impede os cards de aparecerem nos vãos
 * (margens) entre os blocos fixados. Retorna 0 (nada é fixado) quando desabilitado,
 * sem irmãos anteriores ou se o bloco for grande demais.
 */
function useMobileStickyHeader(ref, enabled, zIndex) {
    const [headerHeight, setHeaderHeight] = React.useState(0);
    const [, setTick] = React.useState(0);
    const sigRef = React.useRef('');

    // Reavalia a cada render (filtros/chips mudam), como `useMobileBoundedList`.
    React.useLayoutEffect(() => {
        const root = ref.current;
        if (!enabled || !root) {
            setHeaderHeight((cur) => (cur === 0 ? cur : 0));
            return undefined;
        }
        const items = [];
        for (let sib = root.previousElementSibling; sib; sib = sib.previousElementSibling) {
            const cs = window.getComputedStyle(sib);
            if (cs.position === 'fixed' || cs.position === 'absolute' || sib.offsetHeight === 0) continue;
            items.unshift({ el: sib, h: sib.offsetHeight, mt: px(cs.marginTop), mb: px(cs.marginBottom) });
        }
        sigRef.current = items.map((item) => item.h).join(',');
        let total = 0;
        items.forEach((item, i) => {
            item.offset = total;
            const next = items[i + 1];
            total += item.h + (next ? Math.max(item.mb, next.mt) : item.mb);
        });
        if (items.length === 0 || total > window.innerHeight * MAX_STICKY_HEADER_RATIO) {
            setHeaderHeight((cur) => (cur === 0 ? cur : 0));
            return undefined;
        }
        const saved = items.map(({ el }) => ({ el, position: el.style.position, top: el.style.top, zIndex: el.style.zIndex }));
        items.forEach(({ el, offset }) => {
            el.style.position = 'sticky';
            el.style.top = `calc(${APP_BAR_OFFSET} + ${offset}px)`;
            el.style.zIndex = String(zIndex);
        });
        setHeaderHeight((cur) => (cur === total ? cur : total));
        return () => {
            saved.forEach(({ el, position, top, zIndex: z }) => {
                el.style.position = position;
                el.style.top = top;
                el.style.zIndex = z;
            });
        };
    });

    // Bloco do topo mudou de tamanho sem novo render desta tabela (ex.: chips quebram linha).
    React.useEffect(() => {
        const root = ref.current;
        if (!enabled || !root || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => {
            const sig = [];
            for (let sib = root.previousElementSibling; sib; sib = sib.previousElementSibling) {
                const position = window.getComputedStyle(sib).position;
                if (position !== 'fixed' && position !== 'absolute' && sib.offsetHeight > 0) sig.unshift(sib.offsetHeight);
            }
            if (sig.join(',') !== sigRef.current) setTick((t) => t + 1);
        });
        for (let sib = root.previousElementSibling; sib; sib = sib.previousElementSibling) observer.observe(sib);
        return () => observer.disconnect();
    });

    React.useEffect(() => {
        if (!enabled) return undefined;
        const onResize = () => setTick((t) => t + 1);
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, [enabled]);

    return headerHeight;
}

export default function DefaultTable({
    colunms,
    data, 
    iconsActions, 
    loading = false, 
    sx, 
    onRowClick, 
    rowPerPage = 7 ,
    rowsPerPageOptions=[5, 7, 10, 15],
    itemLabel = 'registros',
    searchValue = '',
    onSearchChange,
    searchPlaceholder = 'Buscar...',
    search = false,
    sortableFields = [],
    defaultSort = null,
    embedded = false,
    // Paginação no servidor (opcional): { page (0-based), rowsPerPage, total,
    // onPageChange(newPage), onRowsPerPageChange(newSize) }. Quando informado,
    // `data` já é a página corrente — a tabela não fatia nem reordena localmente.
    serverPagination = null,
    // Opcional: substitui o card mobile genérico. `(row, { menu, onRowClick }) => ReactNode`;
    // `menu` é o menu de ações da linha (ou null). Desktop não é afetado.
    mobileCardRenderer = null
}) {
    // Verificar se iconsActions existe e não está vazio. Coagido para
    // booleano (`!!`): quando `iconsActions` é `undefined` (tabela sem ações,
    // ex. tabelas de dashboard), `hasActions` também ficava `undefined` — e
    // como `TableSkeleton` usa um default de parâmetro (`hasActions = true`),
    // que só se aplica quando o valor recebido é exatamente `undefined`, a
    // skeleton renderizava uma coluna "Ações" fantasma que a tabela carregada
    // nunca tem.
    const hasActions = !!(iconsActions && Array.isArray(iconsActions) && iconsActions.length > 0);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(rowPerPage);
    const [sortField, setSortField] = React.useState(defaultSort?.field || null);
    const [sortDirection, setSortDirection] = React.useState(defaultSort?.direction || 'asc');
    
    const theme = useTheme();
    const isMobile = useIsMobileShell();
    // Mobile sem paginação no servidor: "Carregar mais" acumula cards em vez de paginar.
    const [mobileVisible, setMobileVisible] = React.useState(rowPerPage);

    const handleChangePage = (event, newPage) => {
        if (serverPagination) {
            serverPagination.onPageChange(newPage);
            return;
        }
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        // Segundo argumento de `parseInt` é o radix (base numérica), não um
        // fallback — passar `rowPerPage` (ex.: 8) aqui fazia o parse em base
        // 8 e corrompia silenciosamente qualquer opção escolhida que não
        // fosse compatível com base 10 (ex.: "25" em base 8 vira 21).
        const newRowsPerPage = parseInt(event.target.value, 10);
        if (serverPagination) {
            serverPagination.onRowsPerPageChange(newRowsPerPage);
            return;
        }
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    // Função para ordenar os dados
    const sortData = (data, field, direction) => {
        if (!field || sortableFields.length === 0) {
            return data;
        }

        return [...data].sort((a, b) => {
            let aValue = a[field];
            let bValue = b[field];

            // Tratar valores nulos/undefined
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';

            // Converter para string para comparação
            aValue = String(aValue).toLowerCase();
            bValue = String(bValue).toLowerCase();

            if (direction === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    };

    // Handler para clique na ordenação
    const handleSort = (field) => {
        if (!sortableFields.includes(field)) {
            return;
        }

        if (sortField === field) {
            // Se já está ordenando por este campo, inverte a direção
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Se é um novo campo, define como ascendente
            setSortField(field);
            setSortDirection('asc');
        }
        setPage(0); // Volta para a primeira página
    };

    // Garantir que data seja sempre um array
    const safeData = Array.isArray(data) ? data : [];

    // Aplicar ordenação se configurada
    const sortedData = sortData(safeData, sortField, sortDirection);

    // `data` é filtrada/recarregada pelo componente pai (ex.: toolbar de
    // filtros de uma tela de lista) sem que este componente saiba disso —
    // sem este ajuste, ficar na página 3 e então aplicar um filtro que deixa
    // só 1 página de resultado renderiza uma tabela "vazia" (linhas de uma
    // página que não existe mais), embora o total de resultados exibido
    // esteja correto. Mantém a página atual quando ainda é válida.
    const totalCount = serverPagination ? serverPagination.total : safeData.length;
    const currentRowsPerPage = serverPagination ? serverPagination.rowsPerPage : rowsPerPage;
    const maxPageIndex = Math.max(0, Math.ceil(totalCount / currentRowsPerPage) - 1);
    React.useEffect(() => {
        if (serverPagination) return;
        setPage((current) => (current > maxPageIndex ? maxPageIndex : current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeData.length, rowsPerPage]);
    const effectivePage = serverPagination ? serverPagination.page : Math.min(page, maxPageIndex);

    const startIndex = effectivePage * currentRowsPerPage;
    const endIndex = startIndex + currentRowsPerPage;
    const paginatedData = serverPagination ? sortedData : sortedData.slice(startIndex, endIndex);

    // ----- Mobile: scroll infinito -----
    // Paginação no servidor: o pai entrega só a página corrente em `data`
    // (e troca por outra ao mudar `serverPagination.page`). Aqui acumulamos as
    // páginas recebidas (`base` = páginas anteriores, `last` = página atual) e
    // pedimos a próxima via `onPageChange` ao chegar no fim. Página 0 (novo
    // filtro/busca) ou salto de página reiniciam o acumulado.
    const serverPage = serverPagination ? serverPagination.page : 0;
    const serverTotalPages = Math.max(1, Math.ceil(totalCount / currentRowsPerPage));
    const [acc, setAcc] = React.useState({ base: [], last: [], page: -1 });
    const lastSrcRef = React.useRef(null);
    const requestedPageRef = React.useRef(-1);
    const sentinelRef = React.useRef(null);
    const listRef = React.useRef(null);
    // Altura (px) do container rolável da lista no mobile; `null` = sem limite
    // (a página rola normalmente). Ver `useMobileBoundedList`.
    const { pathname } = useLocation();
    const bottomNavHidden = !isBottomNavVisible(pathname);
    // Barra oculta: lista fluida (sem altura limitada); a janela rola.
    const listHeight = useMobileBoundedList(listRef, isMobile && !embedded && !bottomNavHidden);
    // Barra oculta: topo (cabeçalho/filtros/chips + busca/contador) fixo abaixo do AppBar.
    const rootRef = React.useRef(null);
    const stickyZ = theme.zIndex.appBar - 3;
    const stickyEnabled = isMobile && !embedded && bottomNavHidden;
    const stickyHeaderHeight = useMobileStickyHeader(rootRef, stickyEnabled, stickyZ);

    React.useLayoutEffect(() => {
        if (!isMobile || !serverPagination) {
            lastSrcRef.current = null;
            return;
        }
        if (loading) return;
        // Mesma referência já processada (ex.: página mudou mas o pai ainda não
        // recarregou): não trata dados antigos como se fossem da página nova.
        if (lastSrcRef.current === data) return;
        lastSrcRef.current = data;
        requestedPageRef.current = -1;
        const rows = Array.isArray(data) ? data : [];
        setAcc((cur) => {
            if (cur.page >= 0 && serverPage === cur.page + 1) {
                const base = [...cur.base, ...cur.last];
                const seen = new Set(base.map((r) => r.id).filter((id) => id != null));
                return { base, last: rows.filter((r) => r.id == null || !seen.has(r.id)), page: serverPage };
            }
            if (cur.page >= 0 && serverPage === cur.page) {
                return { ...cur, last: rows };
            }
            return { base: [], last: rows, page: serverPage };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading, serverPage, isMobile]);

    const hasIntersectionObserver = typeof IntersectionObserver !== 'undefined';
    const accValid = !!serverPagination && acc.page === serverPage;
    // Próxima página já pedida e ainda não chegou (o pai pode levar um render
    // para ligar `loading`; nesse intervalo `data` ainda é a página antiga).
    const loadingMore = !!serverPagination && serverPage > 0 && acc.page === serverPage - 1
        && (loading || lastSrcRef.current === data);
    const mobileServerRows = serverPagination && (accValid || loadingMore) ? [...acc.base, ...acc.last] : safeData;
    const mobileDisplayCount = serverPagination ? mobileServerRows.length : Math.min(mobileVisible, safeData.length);
    const canLoadMore = isMobile && !loading && (serverPagination
        ? accValid && serverPage + 1 < serverTotalPages
        : mobileVisible < safeData.length);

    const loadMore = () => {
        if (serverPagination) {
            const next = serverPage + 1;
            if (requestedPageRef.current === next) return;
            requestedPageRef.current = next;
            serverPagination.onPageChange(next);
            return;
        }
        setMobileVisible((current) => current + currentRowsPerPage);
    };

    React.useEffect(() => {
        if (!canLoadMore || !hasIntersectionObserver) return undefined;
        const el = sentinelRef.current;
        if (!el) return undefined;
        // Recriado a cada mudança de quantidade exibida: se a sentinela continuar
        // visível (lista curta), o observer novo dispara e carrega a próxima leva.
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) loadMore();
        // Com a lista limitada em altura, quem rola é o container (root); senão, a viewport.
        }, { root: listHeight != null ? listRef.current : null, rootMargin: '200px 0px' });
        observer.observe(el);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canLoadMore, mobileDisplayCount, serverPage, hasIntersectionObserver, listHeight != null]);

    if (isMobile) {
        const visibleCols = colunms.filter(({ invisible }) => !invisible);
        const mobileData = serverPagination
            ? sortData(mobileServerRows, sortField, sortDirection)
            : sortedData.slice(0, mobileVisible);
        const mobileEmpty = serverPagination ? mobileServerRows.length === 0 : safeData.length === 0;

        return (
            <Box ref={rootRef} sx={{ ...sx, width: '100%' }}>
                {/* Backdrop opaco (altura 0, sticky): cobre os vãos entre os blocos do topo
                    fixados (ver `useMobileStickyHeader`) para os cards não aparecerem por trás. */}
                {stickyEnabled && stickyHeaderHeight > 0 && (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'sticky',
                            top: `calc(${APP_BAR_OFFSET} + ${stickyHeaderHeight}px)`,
                            height: 0,
                            zIndex: stickyZ - 1,
                            pointerEvents: 'none'
                        }}
                    >
                        <Box sx={{ position: 'absolute', left: -16, right: -16, bottom: 0, height: stickyHeaderHeight, bgcolor: 'background.default' }} />
                    </Box>
                )}

                {/* Busca + contador: também fixos (abaixo do bloco do topo) quando a lista é fluida. */}
                <Box
                    sx={stickyEnabled ? {
                        position: 'sticky',
                        top: `calc(${APP_BAR_OFFSET} + ${stickyHeaderHeight}px)`,
                        zIndex: stickyZ,
                        bgcolor: 'background.default',
                        display: 'flow-root',
                        mx: -0.5,
                        px: 0.5
                    } : undefined}
                >
                {search && (
                    <Box sx={{ mb: 1.5 }}>
                        <MobileSearchField value={searchValue} onChange={onSearchChange} placeholder={searchPlaceholder} />
                    </Box>
                )}

                {!(loading && !loadingMore) && !mobileEmpty && (
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', px: 0.5, pb: 1 }}>
                        {totalCount} {itemLabel}
                    </Typography>
                )}
                </Box>

                {/* Container da lista: com altura limitada só ele rola (cabeçalho da tela fica fixo). */}
                <Box
                    ref={listRef}
                    sx={listHeight != null ? {
                        height: listHeight,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehaviorY: 'contain',
                        // Espaço para o FAB (fixed) não cobrir o último card.
                        pb: '88px',
                        boxSizing: 'border-box'
                    } : (bottomNavHidden && isMobile && !embedded ? {
                        // Barra oculta: lista fluida (a página rola). Os 64px somam com o
                        // padding do `<main>` (16px + safe-area) para o FAB não cobrir o último card.
                        pb: '64px'
                    } : undefined)}
                >
                {loading && !loadingMore ? (
                    <MobileSkeleton />
                ) : mobileEmpty ? (
                    <MobileEmptyState />
                ) : (
                    <>
                        <Stack spacing={1}>
                            {mobileData.map((row, index) => mobileCardRenderer ? (
                                <React.Fragment key={row.id ?? index}>
                                    {mobileCardRenderer(row, {
                                        menu: hasActions ? <MobileRowMenu actions={iconsActions} row={row} /> : null,
                                        onRowClick
                                    })}
                                </React.Fragment>
                            ) : (
                                <MobileCard
                                    key={row.id ?? index}
                                    row={row}
                                    visibleCols={visibleCols}
                                    iconsActions={iconsActions}
                                    hasActions={hasActions}
                                    onRowClick={onRowClick}
                                />
                            ))}
                        </Stack>

                        {loadingMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} sx={{ color: MOBILE_ACCENT }} />
                            </Box>
                        )}

                        {canLoadMore && (hasIntersectionObserver ? (
                            // Sentinela: ao entrar na viewport (com margem de 200px) carrega a próxima leva.
                            <Box ref={sentinelRef} aria-hidden sx={{ height: 1, mt: 1 }} />
                        ) : (
                            // Fallback sem IntersectionObserver: botão manual.
                            <Box
                                component="button"
                                type="button"
                                onClick={loadMore}
                                sx={{
                                    mt: 2,
                                    width: '100%',
                                    minHeight: 44,
                                    borderRadius: '12px',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'background.paper',
                                    color: MOBILE_ACCENT,
                                    fontFamily: 'inherit',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Carregar mais
                            </Box>
                        ))}
                    </>
                )}
                </Box>
            </Box>
        );
    }

    if (loading) {
        return <TableSkeleton colunms={colunms} hasActions={hasActions} embedded={embedded} />;
    }

    if (safeData.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 6,
                bgcolor: embedded ? 'transparent' : 'background.paper',
                borderRadius: embedded ? 0 : (t) => `${t.shape.borderRadius}px`,
                boxShadow: 'none',
                border: embedded ? 'none' : '1px solid',
                borderColor: 'divider'
            }}>
                <Box sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    border: '2px dashed',
                    borderColor: 'divider',
                    color: 'text.disabled',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                }}>
                    <Inbox size={30} strokeWidth={1.5} />
                </Box>
                <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 700, fontSize: '16px' }}>
                    Nenhum registro encontrado
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 320 }}>
                    Tente ajustar os filtros ou adicionar novos registros para visualizar os dados
                </Typography>
            </Box>
        );
    }


    return (
        <Paper sx={{
            ...sx,
            borderRadius: embedded ? 0 : (t) => `${t.shape.borderRadius}px`,
            boxShadow: 'none',
            overflow: 'hidden',
            border: embedded ? 'none' : '1px solid',
            borderColor: 'divider'
        }}>
            {search && (
                <Box sx={{
                    px: 2,
                    py: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    ...(!embedded && {
                        backgroundColor: 'background.subtle',
                    }),
                }}>
                    {/* Campo de busca no lado esquerdo */}
                    {search && (
                        <Box sx={{ flex: 1, maxWidth: 300 }}>
                            <TextField
                                variant="standard"
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon
                                            sx={{
                                                mr: 1,
                                                color: 'text.secondary',
                                                fontSize: 20
                                            }}
                                        />
                                    ),
                                    sx: {
                                        '&:before': {
                                            borderBottom: '1px solid',
                                            borderColor: 'divider'
                                        },
                                        '&:hover:before': {
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main'
                                        },
                                        '&:after': {
                                            borderBottom: '2px solid',
                                            borderColor: 'primary.main'
                                        }
                                    }
                                }}
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-input': {
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: 'text.primary'
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}
            <TableContainer 
                sx={{ 
                    maxHeight: '600px', 
                    overflow: 'auto',
                    position: 'relative',
                    '& .MuiTableHead-root': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: theme.palette.background.paper
                    },
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
                        }
                    }
                }}
            >
                <Table stickyHeader sx={{ position: 'relative' }}>
                    <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <TableRow
                            sx={{
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    left: '16px',
                                    right: '16px',
                                    bottom: 0,
                                    height: '1px',
                                    backgroundColor: 'divider'
                                }
                            }}
                        >
                            {colunms.map(({ path, name, style, invisible }) => (
                                !invisible && (
                                    <TableCell
                                        key={path}
                                        style={style}
                                        sx={{
                                            backgroundColor: 'transparent',
                                            color: 'text.disabled',
                                            fontWeight: 700,
                                            fontSize: '10.5px',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            borderBottom: 'none',
                                            py: 1.75,
                                            px: 2,
                                            cursor: sortableFields.includes(path) ? 'pointer' : 'default',
                                            '&:hover': sortableFields.includes(path) ? {
                                                color: 'primary.main',
                                            } : {}
                                        }}
                                        align="center"
                                        onClick={() => handleSort(path)}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            gap: 0.5
                                        }}>
                                            {name}
                                            {sortableFields.includes(path) && (
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    ml: 0.5
                                                }}>
                                                    {sortField === path ? (
                                                        sortDirection === 'asc' ? (
                                                            <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                        ) : (
                                                            <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                        )
                                                    ) : (
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column',
                                                            opacity: 0.3
                                                        }}>
                                                            <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                                                            <ArrowDownwardIcon sx={{ fontSize: 12, mt: -0.5 }} />
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                )
                            ))}
                            {hasActions && (
                                <TableCell
                                    align="center"
                                    sx={{
                                        backgroundColor: 'transparent',
                                        color: 'text.disabled',
                                        fontWeight: 700,
                                        fontSize: '10.5px',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        borderBottom: 'none',
                                        py: 1.75,
                                        px: 2,
                                    }}
                                >
                                    Ações
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.map((row, index) => {
                            const isLastRow = index === paginatedData.length - 1;
                            return (
                            <TableRow
                                key={row.id}
                                hover
                                sx={{
                                    position: 'relative',
                                    transition: 'background-color 0.12s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: 'background.subtle',
                                    },
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    ...(!isLastRow && {
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: '16px',
                                            right: '16px',
                                            bottom: 0,
                                            height: '1px',
                                            backgroundColor: 'divider'
                                        }
                                    })
                                }}
                                onClick={() => {
                                    if (onRowClick) {
                                        onRowClick(row);
                                    }
                                }}
                            >
                                {colunms.map(({ path, render, style, invisible }) => (
                                    !invisible && (
                                        <TableCell
                                            key={path}
                                            style={style}
                                            align="center"
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                fontSize: '13px',
                                                color: 'text.primary',
                                                borderBottom: 'none',
                                                fontWeight: 400
                                            }}
                                        >
                                            {render ? render(row) : row[path]}
                                        </TableCell>
                                    )
                                ))}
                                {hasActions && (
                                    <TableCell
                                        align="center"
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            borderBottom: 'none'
                                        }}
                                    >
                                        <RowActions actions={iconsActions} row={row} />
                                    </TableCell>
                                )}
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <PaginationFooter
                count={totalCount}
                page={effectivePage}
                rowsPerPage={currentRowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={(newPage) => handleChangePage(null, newPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
                itemLabel={itemLabel}
            />
        </Paper>
    );
}